import React, { useState, KeyboardEvent, ChangeEvent } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface EmailTagInputProps {
  value: string[];
  onChange: (emails: string[]) => void;
  placeholder?: string;
  className?: string;
  id?: string;
  disabled?: boolean;
}

export const EmailTagInput: React.FC<EmailTagInputProps> = ({
  value = [],
  onChange,
  placeholder = "Type email and press Enter",
  className,
  id,
  disabled = false
}) => {
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addEmail();
    } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      // Remove last email if backspace is pressed with empty input
      removeEmail(value.length - 1);
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);
    setError('');
    
    // Auto-add email if user types comma or semicolon
    if (val.endsWith(',') || val.endsWith(';')) {
      const email = val.slice(0, -1).trim();
      if (email) {
        setInputValue(email);
        setTimeout(addEmail, 0);
      } else {
        setInputValue('');
      }
    }
  };

  const addEmail = () => {
    const email = inputValue.trim();
    
    if (!email) {
return;
}
    
    if (!validateEmail(email)) {
      setError('Invalid email address');
      return;
    }
    
    if (value.includes(email)) {
      setError('Email already added');
      return;
    }
    
    onChange([...value, email]);
    setInputValue('');
    setError('');
  };

  const removeEmail = (index: number) => {
    const newEmails = value.filter((_, i) => i !== index);
    onChange(newEmails);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const emails = pastedText.split(/[,;\s]+/).filter(email => email.trim());
    
    const validEmails: string[] = [];
    let hasInvalid = false;
    
    emails.forEach(email => {
      const trimmed = email.trim();
      if (validateEmail(trimmed) && !value.includes(trimmed) && !validEmails.includes(trimmed)) {
        validEmails.push(trimmed);
      } else if (trimmed && !validateEmail(trimmed)) {
        hasInvalid = true;
      }
    });
    
    if (validEmails.length > 0) {
      onChange([...value, ...validEmails]);
    }
    
    if (hasInvalid) {
      setError('Some emails were invalid and not added');
      setTimeout(() => setError(''), 3000);
    }
  };

  return (
    <div className={cn("w-full", className)}>
      <div className="flex flex-wrap gap-2 p-2 border rounded-md min-h-[42px] focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
        {value.map((email, index) => (
          <span
            key={index}
            className="inline-flex items-center gap-1 px-2 py-1 text-sm bg-primary/10 text-primary rounded-md"
          >
            {email}
            <button
              type="button"
              onClick={() => removeEmail(index)}
              disabled={disabled}
              className="hover:bg-primary/20 rounded p-0.5"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          id={id}
          type="email"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={value.length === 0 ? placeholder : "Add another email..."}
          className="flex-1 min-w-[200px] outline-none bg-transparent text-sm"
          disabled={disabled}
        />
      </div>
      {error && (
        <p className="text-xs text-red-500 mt-1">{error}</p>
      )}
      {value.length > 0 && (
        <p className="text-xs text-muted-foreground mt-1">
          {value.length} recipient{value.length !== 1 ? 's' : ''} added
        </p>
      )}
    </div>
  );
};
